import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Chip,
  IconButton,
  Stack,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import PageHeader from '../../components/layout/PageHeader';
import ExpositionFormModal from '../../components/exposition/ExpositionFormModal';
import api from '../../services/api';

const CONTAINER_SX = {
  maxWidth: 1600,
  margin: '0 auto',
  px: { xs: 2, md: 3 },
  py: 3,
};

const dataGridStyles = {
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #eee',
  },
  '& .MuiDataGrid-columnSeparator': { display: 'none' },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
};

export default function Home() {
  const navigate = useNavigate();

  const [expositions, setExpositions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [expositionToEdit, setExpositionToEdit] = useState(null);

  const openCreate = () => {
    setExpositionToEdit(null);
    setModalOpen(true);
  };

  const openEdit = (e) => {
    setExpositionToEdit(e);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este local?')) return;
    try {
      await api.delete(`/expositions/${id}`);
      alert('Local excluído.');
      setExpositions((prev) => prev.filter((x) => x.id !== id));
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Erro ao excluir.');
    }
  };

  const loadExpositions = async () => {
    if (!isAuthenticated) return;
    try {
      const response = await api.get('/expositions');
      setExpositions(response.data);
    } catch (err) {
      console.error(err);
      alert('Falha ao carregar exposições, tente recarregar a página.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const isLogged = localStorage.getItem('token');
    if (!isLogged) navigate('/');
    else setIsAuthenticated(true);
  }, [navigate]);

  useEffect(() => {
    loadExpositions();
  }, [isAuthenticated]);

  const getTotalPoints = (exposition) => (
    (exposition.categories || []).reduce((sum, c) => sum + (c.points?.length || 0), 0)
  );

  const rows = expositions.map((e) => ({
    id: e.id,
    name: e.name,
    address: [e.address, e.city].filter(Boolean).join(' - '),
    categories: (e.categories || []).length,
    points: getTotalPoints(e),
    raw: e,
  }));

  const columns = [
    {
      field: 'name', headerName: 'Nome', flex: 2, minWidth: 200,
    },
    {
      field: 'address', headerName: 'Endereço', flex: 3, minWidth: 240,
    },
    {
      field: 'categories',
      headerName: 'Categorias',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => <Chip label={p.value} size="small" variant="outlined" />,
    },
    {
      field: 'points',
      headerName: 'Pontos',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => <Chip label={p.value} size="small" variant="outlined" />,
    },
    {
      field: 'action',
      headerName: 'Ações',
      width: 160,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            aria-label="Ver detalhes"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/expositions/details', {
                state: { expositionId: p.row.id },
              });
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Editar"
            onClick={(e) => {
              e.stopPropagation();
              openEdit(p.row.raw);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Excluir"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(p.row.id);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={CONTAINER_SX}>
      <PageHeader
        title="Locais"
        description="Espaços físicos cadastrados no OpenSenses. Cada local reúne suas categorias e pontos GPS/beacon usados pela navegação indoor."
        action={(
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            Novo local
          </Button>
        )}
      />

      <Paper elevation={1} sx={{ borderRadius: 2, border: '1px solid #eee', overflow: 'hidden' }}>
        {isLoading ? (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rectangular" height={48} />
            ))}
          </Stack>
        ) : (
          <DataGrid
            sx={dataGridStyles}
            autoHeight
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            onRowClick={(params) => navigate('/expositions/details', {
              state: { expositionId: params.row.id },
            })}
          />
        )}
      </Paper>

      <ExpositionFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        expositionToEdit={expositionToEdit}
        onSaved={loadExpositions}
      />
    </Box>
  );
}
